import React, { useEffect, useState } from "react";
import { SectionWrapper } from "../components/SectionWrapper";
import { Button } from "../components/Button";
import { Input, notification, Spin } from "antd";
import CopyIcon from "../icons/copy-icon.svg";
import RefreshIcon from "../icons/refresh.svg";
import RecapLogo from "../icons/recap-logo.svg";
import RecapKeyService from "@services/RecapKey.service";
import { LoadingOutlined } from "@ant-design/icons";
import classNames from "classnames";
import Loader from "@appComponents/Loader/Loader";

type ApiManagementProps = {
  investorId?: number;
};

export const ApiManagement = ({ investorId }: ApiManagementProps) => {
  const [recapApiKey, setRecapApiKey] = useState<string | null>(null);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(recapApiKey ?? "");
      notification.success({
        message: "API key copied to clipboard",
      });
    } catch (err) {
      notification.error({
        message: "Failed to copy API key to clipboard",
      });
    }
  };

  const generateRecapKey = async () => {
    setIsGeneratingKey(true);
    await RecapKeyService.generateKey(investorId).then((resp) => {
      setIsGeneratingKey(false);
      setRecapApiKey(resp.data.recap_api_key);
    });
  };

  const revokeKey = async () => {
    setIsLoading(true);
    await RecapKeyService.revokeKey(investorId).then(() => {
      setIsLoading(false);
      setRecapApiKey(null);
    });
  };

  useEffect(() => {
    if (investorId) {
      RecapKeyService.getKey(investorId).then((resp) => {
        setRecapApiKey(resp.data.recap_api_key);
        setIsLoading(false);
      });
    }
  }, [investorId]);

  return (
    <SectionWrapper title="Recap tax solutions" sectionAction={<RecapLogo />}>
      <p>
        Bnk To The Future has partnered with Recap so that you can connect your
        BF account seamlessly with Recap in order to generate your Crypto tax
        reports in an instant.
      </p>

      <p>
        Don’t have recap account yet?{" "}
        <a
          href="https://recap.io/"
          target="_blank"
          className="underline font-semibold"
        >
          Create account
        </a>
      </p>

      {isLoading && <Loader />}

      {!isLoading && (
        <div className="flex gap-2 pt-[50px] flex-col lg:flex-row">
          {isGeneratingKey && (
            <div className="flex-1">
              <p>Generating</p>
              <Spin
                indicator={
                  <LoadingOutlined style={{ fontSize: 32 }} spin color="gray" />
                }
              />
            </div>
          )}
          {!isGeneratingKey && (
            <div className="flex-1">
              <Input
                className={classNames("w-full", {
                  "tailwind-antd-input-wrapper": !!recapApiKey,
                  "tailwind-antd-input": !recapApiKey,
                })}
                value={recapApiKey ?? ""}
                readOnly
                placeholder="No generated API yet"
                addonAfter={
                  recapApiKey && <CopyIcon onClick={copyToClipboard} />
                }
              />
              {!!recapApiKey && (
                <a
                  onClick={revokeKey}
                  className="font-semibold text-sm underline text-[#E02020] mt-3 inline-block"
                >
                  Revoke API key
                </a>
              )}
            </div>
          )}

          <Button
            className="h-[54px] flex items-center gap-2"
            isPrimary
            onClick={generateRecapKey}
            disabled={!!recapApiKey || isGeneratingKey}
          >
            Generate a View-Only API key
            <RefreshIcon />
          </Button>
        </div>
      )}
    </SectionWrapper>
  );
};
